<template>
  <div
    :ref="id"
    :class="`input-container${inputSize === 'small' ? ' input-container-sm' : ''}`"
  >
    <input
      v-bind="{ ...$attrs, ...field }"
      :id="id"
      v-model="inputValue"
      :name="name"
      :type="type"
      placeholder=""
      :required="!!rule"
      :maxlength="maximumLength"
      :autocomplete="autocomplete"
      :aria-invalid="isInvalid"
      :class="[endPaddingClass, {
        'is-input-confirmed': confirm,
      }]"
      :disabled="getDisableState || loading"
      @focus.prevent="showClearIcon"
      @input="onInput()"
      @keypress.enter="onEnterBasic($event)"
      @blur="onBlur"
      @focus="onFocus"
    >
    <label
      class="label-top"
      :for="id"
    >
      {{ label }}
    </label>
    <div
      :class="textType"
      class="input-inner-end"
    >
      <button
        v-show="showClear"
        type="button"
        class="clear-icon"
        aria-label="clear text"
        @pointerdown.prevent="onClearClick"
        @blur.prevent="onBlur($event)"
        @keypress.enter="onClearClick"
      >
        <Icon :icon="csgClear" />
      </button>
      <!-- TODO: Should we continue to allow icon classes or use component here? -->
      <template v-if="hasCustomIconData && !showClear && icon && !disabled">
        <slot name="customIcon" />
      </template>

      <template v-else>
        <button
          v-if="!showClear && icon && !disabled "
          :class="icon"
          type="button"
          :aria-label="icon"
          @click.prevent="onIconClickBasic"
        />
      </template>
      <div>
        <button
          v-if="text"
          class="input-inline-cta"
          type="button"
          :disabled="loading"
          :aria-disabled="loading"
          :aria-label="loading ? 'Action is progress, please wait.' : text"
          @click.prevent="$emit('onTextClick', inputValue)"
          @blur="onBlur"
        >
          <Spinner
            v-if="loading"
            class="spinner"
            :style="{ 'right': `calc(50% - ${spinnerRightOffset}px)` }"
            :color="textType === 'primary' ? 'white' : 'black'"
          />
          <!-- hiding text in DOM when in loading state to keep width of an element -->
          <span :style="{ visibility: loading ? 'hidden' : undefined }">{{ text }}</span>
        </button>
      </div>
    </div>
  </div>
  <template v-if="isComboBox">
    <ComboBox
      :width="`${inputTextWidth}px`"
      :data="searchHighlightsArray"
      :combo-box-type="comboBoxType"
      @selectedResult="selectedResult"
      @clearSearchResults="clearSearchResults"
    />
  </template>
</template>
<script>
import { phoneFormat, currencyFormat } from '@vue-util/forms/mask-util';
import { csgClear } from '@csgSvgIcons/icons';
import { useSlots } from 'vue';
import Spinner from '../spinner';
import rules from '../inputText/rules.json';
import generateRandomId from '../../utils/randomNumber';
import Icon from '../icon';
import ComboBox from '../comboBox/ComboBox';

export const props = {
  name: String,
  modelValue: [String, Number],
};
export const emits = ['update:modelValue'];

export default {
  name: 'InputTextBasic',
  components: {
    Spinner,
    Icon,
    ComboBox,
  },
  props: {
    id: {
      type: [String, Number],
      default: () => generateRandomId(),
    },
    name: {
      type: String,
      required: true,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    rule: {
      type: String,
      default: '',
    },
    mask: {
      type: [String],
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: [String, Number],
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    textType: {
      type: String,
      default: undefined,
      validator: (value) => value && ['primary'].includes(value),
    },
    inputSize: {
      type: String,
      default: 'large',
    },
    autocomplete: {
      type: String,
      default: 'on',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    confirm: {
      type: Boolean,
      default: false,
    },
    isInvalid: {
      type: Boolean,
      default: false,
    },
    comboBoxType: {
      type: String,
      default: undefined,
      validator: (val) => ['location', 'searchHistory'].includes(val),
    },
    comboBoxData: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'onIconClick', 'onTextClick', 'onClear', 'onEnter', 'blur', 'focus', 'selectedResult', 'clearSearchResults'],
  data() {
    return {
      inputValue: this.modelValue,
      rulesData: rules,
      showClear: false,
      randomId: generateRandomId(),
      field: {},
      csgClear,
      isComboBox: false,
      searchHighlightsArray: this.comboBoxData,
      inputTextWidth: null,
    };
  },
  computed: {
    maximumLength() {
      if (this.mask === 'phoneFormat') {
        return 12;
      }
      return this.rule.length && this.rulesData[this.rule]
        ? this.rulesData[this.rule].maxlength
        : '50';
    },
    hasCustomIconData() {
      return useSlots().customIcon ? useSlots().customIcon()[0].children.length : false;
    },
    endPaddingClass() {
      if (this.text) {
        if (this.textType) {
          return this.showClear ? 'padding-end-icon-primary' : 'padding-end-primary';
        }
        return this.showClear ? 'padding-end-icon-secondary' : 'padding-end-secondary';
      }
      if (this.icon) {
        return 'padding-end-icon';
      }
      return this.showClear ? 'padding-end-icon' : '';
    },
    getDisableState() {
      this.showClearIcon();
      return this.disabled;
    },
    spinnerRightOffset() {
      return 16 + (this.showClear ? 12 : 0);
    },
  },
  watch: {
    modelValue(newModelValue) {
      // Updates inputValue when modelValue changes
      if (!!newModelValue || newModelValue === '') {
        this.inputValue = newModelValue;
        this.onInput();
      }
    },
    comboBoxData(newValueArray) {
      this.searchHighlightsArray = [...newValueArray];
      this.isComboBox = this.inputValue.trim().length > 0 && this.searchHighlightsArray.length > 0 && this.showComboBox();
    },
  },
  mounted() {
    this.showClearIcon();
    if (this.showComboBox()) {
      this.handleResize();
      document.addEventListener('click', this.handleOutsideClick);
    }
  },
  unmounted() {
    if (this.showComboBox()) {
      document.removeEventListener('click', this.handleOutsideClick);
      this.ro.unobserve(document.body);
    }
  },
  methods: {
    handleResize() {
      this.ro = new ResizeObserver(this.getInputWidth);
      this.ro.observe(document.body);
    },
    getInputWidth() {
      this.inputTextWidth = this.$refs[this.id]?.clientWidth;
    },
    hasWidth() {
      return this.$refs[this.id]?.clientWidth > 100;
    },
    showClearIcon() {
      // Show clear button only if input has value, width is greater than 100px and input is focused;
      if (this.disabled) {
        this.showClear = false;
        return;
      }
      const inputElement = this.$refs[this.id];
      const isFocused = inputElement && inputElement.contains(document.activeElement);
      if (String(this.inputValue).length > 0 && this.hasWidth() && isFocused) {
        this.showClear = true;
      } else if (String(this.inputValue).length <= 0) {
        this.showClear = false;
      }
    },
    onInput() {
      if (this.mask === 'phoneFormat') {
        this.formatPhone();
      }
      if (this.mask === 'currencyFormat') {
        this.formatCurrency();
      }
      this.$emit('update:modelValue', this.inputValue);
    },
    // Phone number masking
    formatPhone() {
      this.inputValue = phoneFormat(this.inputValue);
    },
    // currency masking
    formatCurrency() {
      this.inputValue = currencyFormat(this.inputValue);
    },
    onClearClick() {
      this.inputValue = '';
      this.$emit('onClear', this.inputValue);
      this.$emit('update:modelValue', this.inputValue);
      this.showClear = false;
    },
    onIconClickBasic() {
      this.$emit('onIconClick', this.inputValue);
    },
    onEnterBasic() {
      this.$emit('onEnter', this.inputValue);
    },
    onBlur(event) {
      if (typeof this.inputValue === 'string') {
        this.inputValue = this.inputValue.trim();
      }
      const inputElement = this.$refs[this.id];
      const isClickedInsideInputElement = inputElement && inputElement.contains(event.relatedTarget);
      if (!isClickedInsideInputElement) {
        this.showClear = false;
        this.$emit('update:modelValue', this.inputValue);
      }
      this.$emit('blur', event);
    },
    onFocus(event) {
      this.isComboBox = this.showComboBox() && this.searchHighlightsArray.length > 0;
      this.$emit('focus', event);
    },
    selectedResult(value) {
      this.inputValue = value.description;
      this.isComboBox = false;
      this.$emit('selectedResult', value);
    },
    handleOutsideClick(event) {
      if (this.$refs[this.id] && !this.$refs[this.id].contains(event.target)) this.isComboBox = false;
    },
    showComboBox() {
      return this.comboBoxType === 'location' || this.comboBoxType === 'searchHistory';
    },
    clearSearchResults() {
      this.$emit('clearSearchResults');
    },
  },
};
</script>
<style lang="scss" scoped>
@import "~@core/csg/src/scss/forms/forms.scss";
@include csg-forms;

.input-inline-cta .spinner {
  position: absolute;
  top: calc(50% - 12px);
}

</style>
